import { Button, Typography, Table, TableBody, TableCell, TableHead, TableRow, Chip, Tooltip } from '@mui/material';
import {
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { getLongNumberStr } from '@/helpers/common';
import { Unit, Lease } from '@urbanmix-tech/shared-js';
import dayjs from 'dayjs';
import { calculateLeaseMetrics, concessionsToMonths } from './PricingTool/comparablesUtils';
import { formatLeaseLengthFromDates } from '@/helpers/leaseHelpers';

type DraftLeaseCardProps = {
  unit: Unit;
  currentLease: Lease;
  draftLease: Lease;
  onDelete: (leaseId: string) => void;
  onEdit?: (draftLease: Lease) => void;
  showConcessionsAsMultiples?: boolean;
  showDifferenceAsPercent?: boolean;
};

export function DraftLeaseCard({ unit, currentLease, draftLease, onDelete, onEdit, showConcessionsAsMultiples = true, showDifferenceAsPercent = true }: DraftLeaseCardProps) {
  const currentMetrics = calculateLeaseMetrics(unit, currentLease);
  const draftMetrics = calculateLeaseMetrics(unit, draftLease);

  if (!currentMetrics || !draftMetrics) {
    return null;
  }

  const rentIncrease = draftMetrics.monthlyRent - currentMetrics.monthlyRent;
  const rentIncreasePercent = (rentIncrease / currentMetrics.monthlyRent) * 100;
  const effectiveRentIncrease = draftMetrics.effectiveMonthlyRent - currentMetrics.effectiveMonthlyRent;
  const effectiveRentIncreasePercent = (effectiveRentIncrease / currentMetrics.effectiveMonthlyRent) * 100;
  const ppsqfIncrease = draftMetrics.effectiveAnnualRentPerSqft - currentMetrics.effectiveAnnualRentPerSqft;
  const ppsqfIncreasePercent = (ppsqfIncrease / currentMetrics.effectiveAnnualRentPerSqft) * 100;

  // Calculate days to expiry for current and draft leases (use leaseEndDate from metrics)
  const currentLeaseEndDate = currentMetrics.leaseEndDate;
  const draftLeaseEndDate = draftMetrics.leaseEndDate;
  const currentDaysToExpiry = currentLeaseEndDate ? dayjs(currentLeaseEndDate).diff(dayjs(), 'day') : 0;
  const draftDaysToExpiry = draftLeaseEndDate ? dayjs(draftLeaseEndDate).diff(dayjs(), 'day') : 0;

  // Check for external approval
  const externalApproval = (draftLease as any).externalApproval;
  const isExternallyApproved = externalApproval?.approved;

  return (
    <div className={`w-full rounded-md border px-6 py-4 mr-8 ${
      isExternallyApproved
        ? 'border-green-600/50 bg-green-900/10'
        : 'border-zinc-600 bg-zinc-900/50'
    }`}>
      {/* Header with unit name and delete button */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Typography variant="h6" className="font-bold text-blue-100">
              {unit.name || unit.id} - Draft Terms
            </Typography>
            {isExternallyApproved && (
              <Tooltip
                title={
                  <div>
                    <div>Externally Approved</div>
                    {externalApproval.approvedBy && (
                      <div>By: {externalApproval.approvedBy}</div>
                    )}
                    {externalApproval.approvedAt && (
                      <div>On: {dayjs(externalApproval.approvedAt).format('MMM D, YYYY h:mm A')}</div>
                    )}
                    {externalApproval.notes && (
                      <div>Notes: {externalApproval.notes}</div>
                    )}
                  </div>
                }
              >
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Externally Approved"
                  color="success"
                  size="small"
                />
              </Tooltip>
            )}
          </div>
          <Typography variant="body2" color="textSecondary" className="text-xs">
            {unit.numBedrooms}BR, {unit.sqft} sqft
          </Typography>
        </div>
        <div className="flex gap-2">
          <Button
            size="small"
            color="primary"
            startIcon={<EditIcon />}
            onClick={() => onEdit?.(draftLease)}
          >
            Edit
          </Button>
          <Button
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => onDelete(draftLease.id)}
          >
            Delete
          </Button>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="overflow-x-auto">
        <Table size="small" sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow className="border-b border-zinc-600">
              <TableCell className="text-xs font-bold text-zinc-300 py-2">Metric</TableCell>
              <TableCell align="right" className="text-xs font-bold text-zinc-300 py-2">Current</TableCell>
              <TableCell align="right" className="text-xs font-bold text-blue-300 py-2">Proposed</TableCell>
              <TableCell align="right" className="text-xs font-bold text-zinc-300 py-2">Change</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Monthly Rent Row */}
            <TableRow className="border-b border-zinc-700/50">
              <TableCell className="text-xs text-zinc-400 py-2">Monthly Rent</TableCell>
              <TableCell align="right" className="text-sm text-zinc-300 py-2">
                ${getLongNumberStr(Math.round(currentMetrics.monthlyRent))}
              </TableCell>
              <TableCell align="right" className="text-sm text-blue-100 font-semibold py-2">
                ${getLongNumberStr(Math.round(draftMetrics.monthlyRent))}
              </TableCell>
              <TableCell align="right" className={`text-sm font-semibold py-2 ${rentIncreasePercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {showDifferenceAsPercent
                  ? `${rentIncreasePercent >= 0 ? '+' : ''}${rentIncreasePercent.toFixed(1)}%`
                  : `${rentIncrease >= 0 ? '+' : ''}$${getLongNumberStr(Math.round(rentIncrease))}`}
              </TableCell>
            </TableRow>

            {/* Annual Rent/Sqft Row */}
            <TableRow className="border-b border-zinc-700/50">
              <TableCell className="text-xs text-zinc-400 py-2">Annual Rent/Sqft</TableCell>
              <TableCell align="right" className="text-sm text-zinc-300 py-2">
                ${currentMetrics.effectiveAnnualRentPerSqft.toFixed(2)}
              </TableCell>
              <TableCell align="right" className="text-sm text-blue-100 font-semibold py-2">
                ${draftMetrics.effectiveAnnualRentPerSqft.toFixed(2)}
              </TableCell>
              <TableCell align="right" className={`text-sm font-semibold py-2 ${ppsqfIncreasePercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {showDifferenceAsPercent
                  ? `${ppsqfIncreasePercent >= 0 ? '+' : ''}${ppsqfIncreasePercent.toFixed(1)}%`
                  : `${ppsqfIncrease >= 0 ? '+' : ''}$${ppsqfIncrease.toFixed(2)}`}
              </TableCell>
            </TableRow>

            {/* Effective Monthly Rent Row */}
            <TableRow className="border-b border-zinc-700/50">
              <TableCell className="text-xs text-zinc-400 py-2">Eff. Monthly Rent</TableCell>
              <TableCell align="right" className="text-sm text-zinc-300 py-2">
                ${getLongNumberStr(Math.round(currentMetrics.effectiveMonthlyRent))}
              </TableCell>
              <TableCell align="right" className="text-sm text-blue-100 font-semibold py-2">
                ${getLongNumberStr(Math.round(draftMetrics.effectiveMonthlyRent))}
              </TableCell>
              <TableCell align="right" className={`text-sm font-semibold py-2 ${effectiveRentIncreasePercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {showDifferenceAsPercent
                  ? `${effectiveRentIncreasePercent >= 0 ? '+' : ''}${effectiveRentIncreasePercent.toFixed(1)}%`
                  : `${effectiveRentIncrease >= 0 ? '+' : ''}$${getLongNumberStr(Math.round(effectiveRentIncrease))}`}
              </TableCell>
            </TableRow>

            {/* Lease Length Row */}
            <TableRow className="border-b border-zinc-700/50">
              <TableCell className="text-xs text-zinc-400 py-2">Lease Length</TableCell>
              <TableCell align="right" className="text-sm text-zinc-300 py-2">
                {formatLeaseLengthFromDates(currentMetrics.leaseStartDate, currentMetrics.leaseEndDate)}
              </TableCell>
              <TableCell align="right" className="text-sm text-blue-100 font-semibold py-2">
                {formatLeaseLengthFromDates(draftMetrics.leaseStartDate, draftMetrics.leaseEndDate)}
              </TableCell>
              <TableCell align="right" className="text-sm text-zinc-400 py-2">
                {
                  (() => { 
                    const diff = Math.round((draftMetrics.leaseLength - currentMetrics.leaseLength) * 2) / 2;
                    return `${diff > 0 ? '+' : ''}${diff} ${Math.abs(diff) === 1 ? 'month' : 'months'}`; 
                  })()
                }
              </TableCell>
            </TableRow>

            {/* Concessions Row */}
            <TableRow className="border-b border-zinc-700/50">
              <TableCell className="text-xs text-zinc-400 py-2">Concessions {showConcessionsAsMultiples ? '(mo)' : '($)'}</TableCell>
              <TableCell align="right" className="text-sm text-zinc-300 py-2">
                {showConcessionsAsMultiples
                  ? `${concessionsToMonths(currentMetrics.concessions, currentMetrics.monthlyRent)} mo`
                  : `$${getLongNumberStr(Math.round(currentMetrics.concessions))}`}
              </TableCell>
              <TableCell align="right" className="text-sm text-blue-100 font-semibold py-2">
                {showConcessionsAsMultiples
                  ? `${concessionsToMonths(draftMetrics.concessions, draftMetrics.monthlyRent)} mo`
                  : `$${getLongNumberStr(Math.round(draftMetrics.concessions))}`}
              </TableCell>
              <TableCell align="right" className={`text-sm font-semibold py-2 ${(draftMetrics.concessions - currentMetrics.concessions) <= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {showConcessionsAsMultiples
                  ? `${(concessionsToMonths(draftMetrics.concessions, draftMetrics.monthlyRent) - concessionsToMonths(currentMetrics.concessions, currentMetrics.monthlyRent)).toFixed(1)} mo`
                  : `$${getLongNumberStr(Math.round(draftMetrics.concessions - currentMetrics.concessions))}`}
              </TableCell>
            </TableRow>

            {/* Lease End Date Row */}
            <TableRow className="border-b border-zinc-700/50">
              <TableCell className="text-xs text-zinc-400 py-2">Lease End Date</TableCell>
              <TableCell align="right" className="text-sm text-zinc-300 py-2">
                {currentLeaseEndDate ? dayjs(currentLeaseEndDate).format('MM/DD/YYYY') : 'N/A'}
              </TableCell>
              <TableCell align="right" className="text-sm text-blue-100 font-semibold py-2">
                {draftLeaseEndDate ? dayjs(draftLeaseEndDate).format('MM/DD/YYYY') : 'N/A'}
              </TableCell>
              <TableCell align="right" className="text-sm text-zinc-400 py-2">
                {currentLeaseEndDate && draftLeaseEndDate
                  ? dayjs(draftLeaseEndDate).diff(dayjs(currentLeaseEndDate), 'day') > 0 ? '+' : ''
                  : ''}
                {currentLeaseEndDate && draftLeaseEndDate
                  ? dayjs(draftLeaseEndDate).diff(dayjs(currentLeaseEndDate), 'day') + ' days'
                  : '—'}
              </TableCell>
            </TableRow>

            {/* Days to Expiry Row */}
            <TableRow>
              <TableCell className="text-xs text-zinc-400 py-2">Days to Expiry</TableCell>
              <TableCell align="right" className="text-sm text-zinc-300 py-2">
                {currentDaysToExpiry}
              </TableCell>
              <TableCell align="right" className="text-sm text-blue-100 font-semibold py-2">
                {draftDaysToExpiry}
              </TableCell>
              <TableCell align="right" className={`text-sm font-semibold py-2 ${draftDaysToExpiry > currentDaysToExpiry ? 'text-green-400' : 'text-red-400'}`}>
                {draftDaysToExpiry > currentDaysToExpiry ? '+' : ''}{draftDaysToExpiry - currentDaysToExpiry}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
